import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { initAppAnalytics, trackPageView, updateUserContext } from '../utils/appAnalytics';

const BACKEND_URL = API_BASE_URL;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Impersonation state - stores the original admin data and impersonated context
  const [impersonation, setImpersonation] = useState(null);
  const [originalAdmin, setOriginalAdmin] = useState(null);
  
  const hasCheckedAuth = useRef(false);
  const isCheckingAuth = useRef(false);

  // Function to check auth from server
  const checkAuthFromServer = useCallback(async (callerInfo = 'unknown') => {
    const traceId = `auth_${Date.now()}`;
    console.log(`[AuthContext][${traceId}] checkAuthFromServer called by: ${callerInfo}`);
    console.log(`[AuthContext][${traceId}] isCheckingAuth: ${isCheckingAuth.current}, hasCheckedAuth: ${hasCheckedAuth.current}`);
    console.log(`[AuthContext][${traceId}] Current cookies:`, document.cookie);
    
    if (isCheckingAuth.current) {
      console.log(`[AuthContext][${traceId}] SKIPPING - already checking auth`);
      return;
    }
    isCheckingAuth.current = true;
    
    try {
      console.log(`[AuthContext][${traceId}] Calling /api/auth/me...`);
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include',
      });

      console.log(`[AuthContext][${traceId}] /api/auth/me response status: ${response.status}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[AuthContext][${traceId}] /api/auth/me FAILED: ${response.status}`, errorText);
        throw new Error(`Not authenticated: ${response.status}`);
      }

      const data = await response.json();
      console.log(`[AuthContext][${traceId}] /api/auth/me SUCCESS:`, {
        hasUser: !!data.user,
        userId: data.user?.user_id,
        userEmail: data.user?.email,
        hasOrg: !!data.organization,
        orgId: data.organization?.organization_id,
        teamRole: data.user?.teamRole
      });
      
      setUser(data.user);
      setOrganization(data.organization);
      return true;
    } catch (error) {
      console.error(`[AuthContext][${traceId}] EXCEPTION in checkAuthFromServer:`, error);
      setUser(null);
      setOrganization(null);
      return false;
    } finally {
      console.log(`[AuthContext][${traceId}] Setting loading=false, hasCheckedAuth=true`);
      setLoading(false);
      hasCheckedAuth.current = true;
      isCheckingAuth.current = false;
    }
  }, []);

  // Check existing session on mount
  useEffect(() => {
    console.log(`[AuthContext] Mount check - hasCheckedAuth: ${hasCheckedAuth.current}, user: ${!!user}, pathname: ${location.pathname}`);
    
    // Skip if already authenticated
    if (hasCheckedAuth.current || user) {
      console.log(`[AuthContext] SKIPPING checkAuthFromServer - already have auth`);
      return;
    }

    console.log(`[AuthContext] Triggering checkAuthFromServer from mount`);
    checkAuthFromServer('mount useEffect');
  }, [checkAuthFromServer, user, location.pathname]);

  // Initialize app analytics when user logs in
  useEffect(() => {
    if (user && organization) {
      initAppAnalytics(user.user_id, organization.organization_id);
      updateUserContext(user.user_id, organization.organization_id);
    }
  }, [user, organization]);

  // Track page views for logged-in users
  useEffect(() => {
    if (user) {
      trackPageView(location.pathname);
    }
  }, [location.pathname, user]);

  const logout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    setUser(null);
    setOrganization(null);
    setImpersonation(null);
    setOriginalAdmin(null);
    hasCheckedAuth.current = false;
  };

  // Function to refresh auth data from server (useful after profile/org updates)
  const refreshAuth = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setOrganization(data.organization);
        return data;
      }
    } catch (error) {
      console.error('Refresh auth error:', error);
    }
    return null;
  };

  // Function to directly update organization (for local updates)
  const updateOrganization = (orgData) => {
    setOrganization(prev => ({ ...prev, ...orgData }));
  };

  // Function to handle login (for email/password auth)
  const login = async (loginData) => {
    // Set user from login response
    const userData = {
      user_id: loginData.user_id,
      email: loginData.email,
      name: loginData.name,
      role: loginData.role,
      organization_id: loginData.organization_id
    };
    setUser(userData);
    
    // Fetch full user and organization data
    await checkAuthFromServer('login');
  };

  /**
   * Handle OAuth login - centralized method for all OAuth callbacks.
   * 
   * @param {Object|null} userData - Full user object from OAuth response (if available)
   * @param {Object|null} orgData - Full organization object from OAuth response (if available)
   * 
   * If userData and orgData are provided (e.g., from Emergent OAuth which returns full objects),
   * sets them directly. Otherwise, fetches from /api/auth/me (for Google/Microsoft OAuth
   * which only provide URL params).
   */
  const loginFromOAuth = async (userData = null, orgData = null) => {
    const traceId = `oauth_${Date.now()}`;
    console.log(`[AuthContext][${traceId}] loginFromOAuth called`, {
      hasUserData: !!userData,
      hasOrgData: !!orgData,
      userId: userData?.user_id,
      userEmail: userData?.email
    });

    if (userData && orgData) {
      // Full data provided - set directly (Emergent OAuth flow)
      console.log(`[AuthContext][${traceId}] Setting auth state directly from OAuth response`);
      setUser(userData);
      setOrganization(orgData);
      setLoading(false);
      hasCheckedAuth.current = true;
      return true;
    }

    // No full data - fetch from server (Google/Microsoft OAuth flow)
    // The session cookie should already be set by the backend redirect
    console.log(`[AuthContext][${traceId}] No full data provided, fetching from /api/auth/me`);
    return await checkAuthFromServer('loginFromOAuth');
  };

  // Start impersonating an organization/user
  const startImpersonation = (impersonationData, orgData, impersonatedUser) => {
    // Save original admin state
    setOriginalAdmin({
      user: { ...user },
      organization: { ...organization },
    });
    
    // Set impersonation context
    setImpersonation(impersonationData);
    
    // Override the current user/org with impersonated values
    // This makes all API calls appear to be from the impersonated user's context
    if (impersonatedUser) {
      setUser({
        ...impersonatedUser,
        _isImpersonated: true,
        _originalRole: user?.role,
      });
    }
    if (orgData) {
      setOrganization({
        ...orgData,
        _isImpersonated: true,
      });
    }
  };

  // Stop impersonating and restore admin state
  const stopImpersonation = () => {
    if (originalAdmin) {
      setUser(originalAdmin.user);
      setOrganization(originalAdmin.organization);
    }
    setImpersonation(null);
    setOriginalAdmin(null);
  };

  // Check if currently impersonating
  const isImpersonating = !!impersonation;

  // Get the real admin user (even while impersonating)
  const getRealUser = () => originalAdmin?.user || user;

  const value = {
    user,
    organization,
    loading,
    login,
    loginFromOAuth,
    logout,
    refreshAuth,
    updateOrganization,
    isAuthenticated: !!user,
    
    // Direct setters for signup flow
    setUser,
    setOrganization,
    setLoading,
    
    // Impersonation
    impersonation,
    isImpersonating,
    startImpersonation,
    stopImpersonation,
    getRealUser,
    originalAdmin,
    
    // Legacy support
    impersonating: impersonation,
    setImpersonating: (data) => {
      if (data) {
        startImpersonation(data, null, null);
      } else {
        stopImpersonation();
      }
    },
    clearImpersonation: stopImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
